// Device-local "quick unlock" (PIN or platform biometric) that gates the
// already-authenticated session cookie on app relaunch. This is a
// convenience lock screen, not a server-trusted credential: the server
// session (authToken cookie) is what actually authorizes API calls, and its
// existing 24h expiry is the real security boundary. A wrong PIN/biometric
// here only stops the UI from being shown -- it can't be used to escalate
// access beyond whatever the still-valid cookie already grants.
//
// The PIN is verified against a PBKDF2-HMAC-SHA256 derivation
// (PIN_KDF_ITERATIONS below), not a bare hash -- anyone who obtains the
// localStorage blob (salt + derived hash + iteration count, all stored
// alongside each other) still cannot brute-force a 4-6 digit PIN nearly as
// cheaply as a single hash round would allow. This raises the cost, it does
// not eliminate it: a PIN's entropy is small enough that this remains
// offline-brute-forceable given enough time, which is why the PIN
// lockout below (MAX_PIN_ATTEMPTS) is a UX nudge, not the real defence --
// it is enforced by the same client that stores the counter it checks, so
// clearing localStorage resets it. The PBKDF2 iteration count is stored per
// PIN (pinKdfIterations) specifically so a config written before this
// derivation existed (a bare SHA-256 hash, unverifiable under PBKDF2 no
// matter what the user types) is recognised and invalidated rather than
// silently mismatching -- see verifyPin.

const STORAGE_KEY = 'quickUnlock';
const PROMPT_DISMISSED_KEY = 'quickUnlockPromptDismissed';
const MAX_PIN_ATTEMPTS = 5;
const RP_NAME = 'Eternal Locker';
// OWASP's current PBKDF2-HMAC-SHA256 guidance (600k+) targets server-side
// password storage verified once per login; this runs in the browser on
// every quick-unlock attempt for a control that only gates the UI, not the
// session (see file header). 100k keeps that interactive cost low (tens of
// milliseconds on typical hardware) while still moving a brute force of a
// short numeric PIN from "microseconds per guess" (one SHA-256 round) to
// meaningfully slower, rather than leaving it free.
const PIN_KDF_ITERATIONS = 100000;

const readConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const writeConfig = (config) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage failures (private browsing/quota).
  }
};

export const getQuickUnlockConfig = () => readConfig();

export const isQuickUnlockEnabled = () => {
  const config = readConfig();
  return !!(config && config.enabled && (config.method === 'pin' || config.method === 'biometric'));
};

export const clearQuickUnlock = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export const isQuickUnlockPromptDismissed = () => {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

export const dismissQuickUnlockPrompt = () => {
  try {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
  } catch {
    // Ignore storage failures.
  }
};

export const shouldOfferQuickUnlockSetup = () => {
  return !isQuickUnlockEnabled() && !isQuickUnlockPromptDismissed();
};

export const isBiometricAvailable = async () => {
  try {
    if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
    if (!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

const toHex = (buffer) => Array.from(new Uint8Array(buffer)).map((b) => b.toString(16).padStart(2, '0')).join('');

const randomBytes = (length) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
};

const hashPin = async (pin, saltHex, iterations) => {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const derivedBits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: encoder.encode(saltHex), iterations, hash: 'SHA-256' },
    keyMaterial,
    256
  );
  return toHex(derivedBits);
};

export const setUpPin = async (pin) => {
  const salt = toHex(randomBytes(16));
  const pinHash = await hashPin(pin, salt, PIN_KDF_ITERATIONS);
  writeConfig({ enabled: true, method: 'pin', pinSalt: salt, pinHash, pinFailCount: 0, pinKdfIterations: PIN_KDF_ITERATIONS });
};

export const verifyPin = async (pin) => {
  const config = readConfig();
  if (!config || config.method !== 'pin' || !config.pinHash || !config.pinSalt) return false;

  // A PIN set up before PBKDF2 was introduced here has no pinKdfIterations
  // marker and was hashed with a single SHA-256 round - it can never verify
  // correctly under the derivation below, no matter what the user types.
  // Rather than let them burn all MAX_PIN_ATTEMPTS against a hash that
  // structurally cannot match, fail the same way "too many attempts"
  // already does: clear the stale config and send them back to password
  // login, the actual security boundary this only ever gated in front of.
  if (!config.pinKdfIterations) {
    clearQuickUnlock();
    throw new Error('LOCKED_OUT');
  }

  const candidateHash = await hashPin(pin, config.pinSalt, config.pinKdfIterations);
  const isValid = candidateHash === config.pinHash;

  if (isValid) {
    writeConfig({ ...config, pinFailCount: 0 });
    return true;
  }

  const pinFailCount = (config.pinFailCount || 0) + 1;
  if (pinFailCount >= MAX_PIN_ATTEMPTS) {
    clearQuickUnlock();
    throw new Error('LOCKED_OUT');
  }
  writeConfig({ ...config, pinFailCount });
  return false;
};

export const getRemainingPinAttempts = () => {
  const config = readConfig();
  const failCount = config?.pinFailCount || 0;
  return Math.max(0, MAX_PIN_ATTEMPTS - failCount);
};

const bufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const base64UrlToBuffer = (base64Url) => {
  const padded = base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(base64Url.length + (4 - (base64Url.length % 4)) % 4, '=');
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Registers a platform-authenticator credential purely to gate this local
// lock screen -- there is no server-side relying-party verification of the
// resulting assertion, since the only thing being protected is whether the
// already-authenticated UI is shown on this device.
export const registerBiometric = async (accountEmail) => {
  if (!window.PublicKeyCredential) throw new Error('WEBAUTHN_UNSUPPORTED');

  const userId = randomBytes(16);
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: RP_NAME },
      user: {
        id: userId,
        name: accountEmail || 'eternal-locker-user',
        displayName: accountEmail || 'Eternal Locker User',
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
      },
      timeout: 60000,
      attestation: 'none',
    },
  });

  if (!credential) throw new Error('BIOMETRIC_REGISTRATION_FAILED');

  writeConfig({ enabled: true, method: 'biometric', credentialId: bufferToBase64Url(credential.rawId) });
};

export const verifyBiometric = async () => {
  const config = readConfig();
  if (!config || config.method !== 'biometric' || !config.credentialId) return false;

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{ id: base64UrlToBuffer(config.credentialId), type: 'public-key' }],
      userVerification: 'required',
      timeout: 60000,
    },
  });

  return !!assertion;
};
